import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API } from "../App";
import axios from "axios";
import { ExternalLink, Building2, Heart, AlertCircle } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const TrackingPage = () => {
  const { trackingCode } = useParams();
  const { t } = useTranslation(['common']);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const recordClick = async () => {
      try {
        const response = await axios.get(`${API}/t/${trackingCode}`);
        setCampaign(response.data.campaign);
      } catch (err) {
        setError(t('common:errors.notFound'));
      } finally {
        setLoading(false);
      }
    };
    recordClick();
  }, [trackingCode, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-[#05060D] flex items-center justify-center p-6 relative">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSwitcher />
        </div>
        
        <div 
          className="fixed inset-0 pointer-events-none opacity-10"
          style={{
            backgroundImage: `url("https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png")`,
            backgroundSize: "300px",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat"
          }}
        />
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
            alt="EleKtro"
            className="w-16 h-16 mx-auto mb-4 opacity-50"
          />
          <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold font-['Unbounded'] text-white mb-2">
            {t('common:errors.notFound')}
          </h1>
          <p className="text-gray-400">
            {error || t('common:errors.linkExpired')}
          </p>
          <a 
            href="/"
            className="mt-6 inline-block text-cyan-400 hover:text-cyan-300"
          >
            {t('common:nav.backToHome')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060D] flex items-center justify-center p-6 relative">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>
      
      {/* Heart Background */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage: `url("https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png")`,
          backgroundSize: "400px",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      />
      
      <div className="glass-card rounded-2xl p-8 max-w-2xl w-full relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_friend-doc-view/artifacts/09b4legs_Conscious%20Creator%20logoet.png"
            alt="EleKtro"
            className="w-10 h-10"
          />
          <div>
            <span className="text-xl font-bold font-['Unbounded'] gradient-text">
              EleKtro Universal
            </span>
            <span className="text-xs text-cyan-400/80 block">Conscious Creator</span>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
            <Building2 className="w-4 h-4" />
            <span>{campaign.business_name}</span>
          </div>
          <h1 className="text-3xl font-bold font-['Unbounded'] text-white mb-4">
            {campaign.title}
          </h1>
          <p className="text-gray-300 text-lg leading-relaxed">
            {campaign.description}
          </p>
        </div>

        {/* Media */}
        {Array.isArray(campaign.media_urls) && campaign.media_urls.length > 0 && (
          <div className="mb-6">
            <div className="grid gap-4">
              {campaign.media_urls.slice(0, 3).map((url, index) => (
                <div key={`media-${index}`} className="relative rounded-xl overflow-hidden bg-white/5">
                  {url && url.match(/\.(mp4|webm|mov)$/i) ? (
                    <video 
                      src={url} 
                      controls 
                      className="w-full rounded-xl"
                    />
                  ) : (
                    <img 
                      src={url} 
                      alt={`Campaign media ${index + 1}`}
                      className="w-full h-auto rounded-xl"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-6 border-t border-white/10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-cyan-400 fill-cyan-400" />
            <p className="text-sm text-gray-500">
              EleKtro Universal · Conscious Creator
            </p>
          </div>
          <div className="text-center">
            <a 
              href="/"
              className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-2"
            >
              {t('common:nav.learnMore')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;
